// Web View Version of EstimatePage
import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardMedia, Container, Skeleton, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import {  useLocation } from 'react-router-dom';
import Slider from 'react-slick';
import { HashLink } from 'react-router-hash-link';
import CommonWarrantySection from './section/commonWarrantySection';
import TextMaxLine from '../../components/text-max-line';
import { mockProductsData } from '../../mock/productsData';
import { fetchDynamicPrices, getMergedProductsData } from '../../utils/pricingService';
import EstimateCard from './EstimateCalculation';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export default function EstimatePageWeb() {
  const location = useLocation();
  const [allProducts, setAllProducts] = useState(mockProductsData);
  const loading = false;
  const locationData = location?.state;

  const [selectedCurtain, setSelectedCurtain] = useState(mockProductsData.products[0]);

  const handleCurtainTypeChange = (item) => {
    setSelectedCurtain(item);
  };

  useEffect(() => {
    let isMounted = true;
    fetchDynamicPrices().then((prices) => {
      if (isMounted && prices) {
        const merged = getMergedProductsData(prices);
        setAllProducts(merged);
        setSelectedCurtain((prev) => {
          const updated = merged.products.find((p) => p._id === prev?._id);
          return updated || prev;
        });
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (locationData) {
      const currentItem = allProducts?.products?.filter((i) => i._id === locationData);
      if (currentItem && currentItem[0]) {
        setSelectedCurtain(currentItem[0]);
      }
      window.scrollTo({ top: 400, behavior: 'smooth' });
    } else if (allProducts?.products && allProducts.products[0]) {
      setSelectedCurtain((prev) => {
        const found = allProducts.products.find((p) => p._id === prev?._id);
        return found || allProducts.products[0];
      });
    }
  }, [allProducts?.products, locationData]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  return (
    <Box px="70px">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Top Return Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2.5,
            mb: 3,
            borderBottom: '1px solid #eaeaea',
          }}
        >
          <Button
            href="https://zealcurtains.com"
            startIcon={<ArrowBack sx={{ fontSize: '18px !important' }} />}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              color: '#1a1a1a',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dcdcdc',
              borderRadius: '24px',
              px: 2.5,
              py: 0.8,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#0f172a',
                color: '#ffffff',
                borderColor: '#0f172a',
              },
            }}
          >
            ← Back to Zeal Curtains Home
          </Button>

          <Box
            component="a"
            href="https://zealcurtains.com"
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <img src="/assets/logo-black.png" alt="Zeal Curtains" style={{ height: 32 }} />
          </Box>
        </Box>

        <Typography variant="h4" fontWeight="bold" mb={3}>
          Instant Estimate
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={3}>
          {/* Left Side - Banner Slider */}
          <Box flex={1} minWidth="500px" height="100%" className="zeal-estimate-banner">
            {selectedCurtain?.banner_image?.length > 0 ? (
              <Slider {...settings} className="banner-web-slider">
                {selectedCurtain?.banner_image.map((img, index) => (
                  <img key={index} src={img.url} alt={`Curtain ${index + 1}`} className="banner-web-image" />
                ))}
              </Slider>
            ) : (
              <Skeleton
                variant="rectangular"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  aspectRatio: '14/9',
                  display: 'block',
                }}
              />
            )}
          </Box>

          {/* Right Side - Estimation Form */}
          <Box flex={1} minWidth="400px">
            <Card sx={{ p: 2, pt: 0, borderRadius: 2, boxShadow: 'none' }}>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                Choose Product
              </Typography>
              <Stack sx={{ textAlign: 'center', p: 0, mb: '.5rem' }}>
                {loading ? (
                  <Stack direction="row" mt={1} spacing={2} sx={{ width: '100%' }}>
                    {[1, 2, 3, 4].map((_, idx) => (
                      <Box key={idx} sx={{ flex: 1, position: 'relative' }}>
                        <Box
                          sx={{
                            width: '100%',
                            paddingTop: '60%',
                            position: 'relative',
                          }}
                        >
                          <Skeleton
                            variant="rectangular"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      overflowX: 'auto',
                      gap: 1.3,
                      px: 0.5,
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    {' '}
                    {allProducts?.products && allProducts.products.length > 0 ? (
                      allProducts?.products?.map((card) => (
                        <Box
                          key={card._id}
                          onClick={() => handleCurtainTypeChange(card)}
                          sx={{
                            flex: '0 0 auto',
                            width: selectedCurtain?._id === card._id ? '125px' : '107px', // Slight expansion on selection
                            textAlign: 'center',
                            border: selectedCurtain?._id === card._id ? '2px solid #ED262980' : '1px solid transparent',
                            borderRadius: selectedCurtain?._id === card._id ? '8px ' : '13px',
                            position: 'relative',
                            cursor: 'pointer',
                            // backgroundColor: '#fff',
                            backgroundColor: selectedCurtain?._id === card?._id ? '#ED262908' : 'none',
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0, pb: 0.5 }}>
                            <CardMedia
                              component="div"
                              sx={{
                                width: '100%',
                                height: '62px',
                                overflow: 'hidden',
                                borderRadius: selectedCurtain?._id === card._id ? '6px 6px 0 0' : '4px',
                              }}
                            >
                              {/* <img
                                src={card.banner_image ? card.banner_image[0] : '/assets/images/noImage/noImage.svg'}
                                alt={card.alt}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              /> */}
                              {card?.banner_image?.length>0 ? (
                                <img
                                  src={
                                    card.banner_image ? card.banner_image[0]?.url : '/assets/images/noImage/noImage.svg'
                                  }
                                  alt={card.alt}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <img
                                  src={'/assets/images/noImage/noImage.svg'}
                                  alt="no Img"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              )}
                            </CardMedia>
                            <TextMaxLine
                              className="roboto-sans-medium"
                              line={2}
                              persistent
                              sx={{
                                px: selectedCurtain?._id === card?._id ? 1 : 0,
                                fontSize: selectedCurtain?._id === card?._id ? '13px' : '13px',
                                // pb: 0.5,
                                pt: 1,
                                color: 'black',
                                // fontSize: '13px',
                                textTransform: 'capitalize',
                                textAlign: 'left',
                                lineHeight: '14px', // Or however tall each line should be
                                height: '36px', // 2 lines * 14px
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {card?.name}
                            </TextMaxLine>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          my: 5,
                          textAlign: 'center',
                          width: '100%',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                        }}
                      >
                        No product found
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>

              <EstimateCard selectedCurtain={selectedCurtain} />
              {/* <Button
              component={Link}
              to="/booking"
              variant="contained"
              sx={{ display: 'flex', alignItems: 'center', boxShadow: 'none', borderRadius: '999px' }}
              className="zeal-btn-solid hidebtn1"
            >
              <Typography className="merri-weather-text-lighter" fontSize="16px">
                {' '}
                Free Material Consultation
              </Typography>
            </Button> */}
              <HashLink smooth to={'/booking#booking_top'} style={{ textDecoration: 'none' }}>
                <Button
                  // component={Link}
                  // to="/booking"
                  variant="contained"
                  color="error"
                  fullWidth
                  sx={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    paddingY: '12px',
                    marginTop: '16px',
                    textTransform: 'none',
                  }}
                >
                  Book a Free Home Visit
                </Button>
              </HashLink>
            </Card>
          </Box>
        </Box>
        <CommonWarrantySection />
      </Container>
    </Box>
  );
}
